import {DiscordSDK, patchUrlMappings} from '@discord/embedded-app-sdk';

export type DiscordParticipant = {
  id: string;
  username: string;
  globalName?: string;
  avatar?: string | null;
  discriminator?: string;
};

export type DiscordContext = {
  authenticated: boolean;
  inCall: boolean;
  instanceId: string;
  channelId?: string;
  guildId?: string;
  user?: DiscordParticipant;
  accessToken?: string;
  participants: DiscordParticipant[];
  sdk?: DiscordSDK;
  onParticipantsChanged(handler: (participants: DiscordParticipant[]) => void): () => void;
  openInviteDialog?: () => Promise<void>;
};

export async function initializeDiscordActivity(
  setStatus: (message: string) => void,
  setParticipantCount: (count: number) => void
): Promise<DiscordContext> {
  const clientId = import.meta.env.VITE_DISCORD_CLIENT_ID as string | undefined;
  const params = new URLSearchParams(window.location.search);
  const listeners = new Set<(p: DiscordParticipant[]) => void>();
  let participants: DiscordParticipant[] = [];

  const ctx: DiscordContext = {
    authenticated: false,
    inCall: false,
    instanceId: params.get('instanceId') ?? params.get('room') ?? `local-${shortRandom()}`,
    participants: [],
    onParticipantsChanged(handler) {
      listeners.add(handler);
      return () => {
        listeners.delete(handler);
      };
    }
  };

  const emitParticipants = (): void => {
    ctx.participants = participants;
    setParticipantCount(participants.length || 1);
    for (const handler of listeners) {
      handler(participants);
    }
  };

  if (!clientId || clientId.includes('replace_with')) {
    const devUserId = params.get('user') ?? `local-${shortRandom()}`;
    const devName = params.get('name') ?? devUserId;
    ctx.user = {id: devUserId, username: devName, globalName: devName};
    participants = [ctx.user];
    setStatus('Local dev mode (no Discord client id).');
    emitParticipants();
    return ctx;
  }

  const discordSdk = new DiscordSDK(clientId);
  ctx.sdk = discordSdk;
  ctx.instanceId = discordSdk.instanceId;
  ctx.channelId = discordSdk.channelId ?? undefined;
  ctx.guildId = discordSdk.guildId ?? undefined;
  ctx.inCall = Boolean(ctx.channelId);
  setStatus('Waiting for Discord...');
  await discordSdk.ready();

  // In production, route WebSocket through Discord's proxy so CORS + firewalls
  // are handled. patchUrlMappings rewrites `new WebSocket(VITE_WS_URL)` to
  // use the discordsays.com iframe proxy. Only apply when actually inside Discord.
  // Only patch when running inside Discord's iframe (proxy is active).
  if (window.parent !== window) {
    const relayUrl = import.meta.env.VITE_WS_URL as string | undefined;
    if (relayUrl && (relayUrl.startsWith('wss://') || relayUrl.startsWith('ws://'))) {
      const relayHost = relayUrl.replace(/^wss?:\/\//, '').split('/')[0];
      patchUrlMappings([{prefix: '/relay', target: relayHost}]);
    }
  }


  setStatus('Authorizing...');
  const {code} = await discordSdk.commands.authorize({
    client_id: clientId,
    response_type: 'code',
    state: shortRandom(),
    prompt: 'none',
    scope: ['identify', 'guilds.members.read', 'rpc.activities.write']
  });

  const tokenResponse = await fetch('/api/token', {
    method: 'POST',
    headers: {'Content-Type': 'application/json'},
    body: JSON.stringify({code})
  });
  if (!tokenResponse.ok) {
    throw new Error(`Token exchange failed: ${tokenResponse.status}`);
  }
  const {access_token: accessToken} = (await tokenResponse.json()) as {access_token: string};
  const auth = await discordSdk.commands.authenticate({access_token: accessToken});
  ctx.accessToken = accessToken;
  ctx.authenticated = true;
  ctx.user = {
    id: auth.user.id,
    username: auth.user.username,
    globalName: auth.user.global_name ?? undefined,
    avatar: auth.user.avatar,
    discriminator: auth.user.discriminator
  };
  setStatus(`Ready as ${ctx.user.globalName ?? ctx.user.username}`);

  ctx.openInviteDialog = async () => {
    const anyCommands = discordSdk.commands as unknown as {openInviteDialog?: () => Promise<void>};
    if (typeof anyCommands.openInviteDialog === 'function') {
      await anyCommands.openInviteDialog();
    } else {
      console.warn('openInviteDialog not available in this SDK version');
    }
  };

  try {
    const initial = await discordSdk.commands.getInstanceConnectedParticipants();
    participants = normalizeParticipants(initial);
  } catch {
    participants = ctx.user ? [ctx.user] : [];
  }
  emitParticipants();

  discordSdk.subscribe('ACTIVITY_INSTANCE_PARTICIPANTS_UPDATE', (event: unknown) => {
    participants = normalizeParticipants(event);
    emitParticipants();
  });

  return ctx;
}

function normalizeParticipants(payload: unknown): DiscordParticipant[] {
  const obj = payload as {participants?: unknown[]} | unknown[] | undefined;
  let list: unknown[] = [];
  if (Array.isArray(obj)) {
    list = obj;
  } else if (obj && Array.isArray((obj as {participants?: unknown[]}).participants)) {
    list = (obj as {participants: unknown[]}).participants;
  }
  return list.map(entry => {
    const p = entry as {
      id: string;
      username: string;
      global_name?: string | null;
      avatar?: string | null;
      discriminator?: string;
    };
    return {
      id: p.id,
      username: p.username,
      globalName: p.global_name ?? undefined,
      avatar: p.avatar ?? null,
      discriminator: p.discriminator
    };
  });
}

function shortRandom(): string {
  return Math.random().toString(36).slice(2, 10);
}
